import Conference from "./Conference";
import JitsiMeetJS from "../JitsiMeetLib";
import { consoleLog } from "../utils/logger";
import { ErrorWithCodes } from "../errors/errors";
import EventEmitter from "../utils/EventEmitter";
import RoomConfig from "./RoomConfig";
import ENUMS from "../constants/enums";

const eventEmitter = new EventEmitter();

/**
 * Remote record session ID. We must keep this data for knowing who started
 * current remote session. If session Id is equal to the incoming event session,
 * means this user started a remote record
 */
const recordSessionID = {
  field: "remote_session_id",
  get() {
    return localStorage.getItem(this.field);
  },
  set(id) {
    localStorage.setItem(this.field, id);
  },
  delete() {
    localStorage.removeItem(this.field);
  },
};

class RemoteRecordingManager {
  constructor() {}

  handleEvents() {
    recordSessionID.delete();
    Conference.room.on(JitsiMeetJS.events.conference.RECORDER_STATE_CHANGED, this.onRecorderStateChanged.bind(this));
  }

  getRecordStartTime() {
    const participants = Conference.room.getParticipants();
    const jibri = participants.find((p) => p.getStatsID() === "jibri" && p.isHidden());
    return jibri?.getProperty("start_time") || null;
  }

  onRecorderStateChanged(response) {
    consoleLog("RECORDER_STATE_CHANGED", response);

    const mode = response.getMode();
    const status = response.getStatus();
    const id = response.getID();
    const error = response.getError();

    const isFile = mode === "file";
    const isOn = status === "on";
    const isOff = status === "off";

    if (isFile && isOn) {
      const showIsRecording = !recordSessionID.get() || recordSessionID.get() === id;
      eventEmitter.emit("RemoteRecordStarted", showIsRecording);
      recordSessionID.set(id);
    } else if (isFile && isOff) {
      let errorCode;
      /**
       * if error object is string 'undefined', that means
       * "there is not enough space in xperdrive for this user"
       */
      if (error) {
        errorCode = error === "undefined" ?  ErrorWithCodes.NOT_ENOUGHT_SPACE : ErrorWithCodes.RECORD_FAILED_TO_START;
        RoomConfig.setProperty(ENUMS.RoomConfigEnums.REMOTE_RECORD_STARTED_BY, null);
        recordSessionID.delete();
      }
      eventEmitter.emit("RemoteRecordStopped", { errorCode });
    }
  }

  startRecording() {
    const appData = JSON.stringify({
      file_recording_metadata: {
        hostName: localStorage.getItem("keycloak-token"),
      },
    });

    return new Promise((resolve, reject) => {
      Conference.room
        .startRecording({
          mode: "file",
          appData,
        })
        .then((response) => {
          const keycloakId = Conference.room.getLocalParticipantProperty("KeycloakId");
          RoomConfig.setProperty(ENUMS.RoomConfigEnums.REMOTE_RECORD_STARTED_BY, keycloakId);
          recordSessionID.set(response.getID());
          resolve();
        })
        .catch(reject);
    });
  }

  stopRecording() {
    return new Promise((resolve, reject) => {
      const sessionID = recordSessionID.get();
      const session = this.getSession(sessionID);

      if (!session) {
        return reject(new Error("Could not find session"));
      }

      session
        .stop({
          focusMucJid: Conference.room.recordingManager._chatRoom.focusMucJid,
        })
        .then(() => {
          resolve();
          recordSessionID.delete();
          RoomConfig.setProperty(ENUMS.RoomConfigEnums.REMOTE_RECORD_STARTED_BY, null);
        })
        .catch(reject);
    });
  }

  getSession(sessionID) {
    return Conference.room.recordingManager._sessions[sessionID];
  }

  getActiveSessions() {
    if (!Conference.room) return [];

    const sessions = Conference.room.recordingManager._sessions;
    const result = [];

    Object.values(sessions).forEach((session) => {
      if (session._status === "on") {
        result.push(session);
      }
    });

    return result;
  }

  isActiveSessionExists() {
    return this.getActiveSessions().length > 0;
  }

  emit(eventType, callbackFn) {
    eventEmitter.emit(eventType, callbackFn);
  }

  on(eventType, callbackFn) {
    eventEmitter.on(eventType, callbackFn);
  }

  off(eventType, callbackFn) {
    eventEmitter.off(eventType, callbackFn);
  }

  clearEvents() {
    eventEmitter.clear();
  }
}

export default new RemoteRecordingManager();
